import React, { useEffect, useRef, useState } from 'react';
import { Camera, X, RefreshCw, Check, Loader2, SwitchCamera } from 'lucide-react';

interface Props {
  onCapture: (file: File) => void;
  onClose: () => void;
  filenamePrefix?: string;
}

/**
 * Modal de capture caméra via `navigator.mediaDevices.getUserMedia`.
 *
 * Pourquoi pas juste `<input capture="environment">` ?
 * De nombreux navigateurs (surtout mobiles avec WebView, PWA en standalone,
 * ou WKWebView iOS) ignorent l'attribut `capture` et ouvrent la galerie —
 * ce qui laisse l'étudiant coincé quand il veut prendre une photo sur place.
 * `getUserMedia` demande une seule fois la permission caméra puis affiche
 * un vrai viseur inline. Fallback : si l'accès caméra est refusé, on ferme
 * la modal et l'utilisateur passera par le bouton "Depuis la galerie".
 */
export const CameraCaptureModal: React.FC<Props> = ({ onCapture, onClose, filenamePrefix = 'photo' }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(true);
  const [preview, setPreview] = useState<string | null>(null);
  const [facing, setFacing] = useState<'environment' | 'user'>('environment');

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const startCamera = async (mode: 'environment' | 'user') => {
    stopStream();
    setStarting(true);
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: mode }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => undefined);
      }
    } catch (err: any) {
      const msg = err?.name === 'NotAllowedError'
        ? 'Autorisation caméra refusée. Utilisez "Depuis la galerie" à la place.'
        : err?.name === 'NotFoundError'
        ? 'Aucune caméra détectée sur cet appareil.'
        : "Impossible de démarrer la caméra. Utilisez la galerie à la place.";
      setError(msg);
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    startCamera(facing);
    return stopStream;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flip = async () => {
    const next = facing === 'environment' ? 'user' : 'environment';
    setFacing(next);
    await startCamera(next);
  };

  const takeShot = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    const w = v.videoWidth;
    const h = v.videoHeight;
    if (!w || !h) return;
    c.width = w;
    c.height = h;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(v, 0, 0, w, h);
    setPreview(c.toDataURL('image/jpeg', 0.92));
    stopStream();
  };

  const retake = async () => {
    setPreview(null);
    await startCamera(facing);
  };

  const validate = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.toBlob((blob) => {
      if (!blob) return;
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      const file = new File([blob], `${filenamePrefix}-${ts}.jpg`, { type: 'image/jpeg' });
      onCapture(file);
    }, 'image/jpeg', 0.92);
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black flex flex-col" onClick={(e) => e.stopPropagation()}>
      {/* Barre haut */}
      <div className="flex items-center justify-between px-3 py-2 bg-black/70 text-white text-sm border-b border-white/10">
        <span className="font-medium">Prendre une photo</span>
        <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded" title="Fermer">
          <X size={18} />
        </button>
      </div>

      {/* Viseur / aperçu */}
      <div className="flex-1 flex items-center justify-center relative bg-black overflow-hidden">
        {preview ? (
          <img src={preview} alt="Aperçu" className="max-w-full max-h-full object-contain" />
        ) : (
          <>
            <video
              ref={videoRef}
              playsInline
              muted
              className="max-w-full max-h-full"
            />
            {starting && (
              <div className="absolute inset-0 flex items-center justify-center text-white gap-2">
                <Loader2 size={18} className="animate-spin" />
                <span className="text-sm">Démarrage de la caméra…</span>
              </div>
            )}
          </>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {error && (
        <div className="px-4 py-2 bg-red-900/40 text-red-200 text-sm text-center">
          {error}
        </div>
      )}

      {/* Barre bas */}
      <div className="flex items-center justify-around px-4 py-4 bg-black/70 border-t border-white/10">
        {preview ? (
          <>
            <button onClick={retake}
              className="flex items-center gap-2 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-full">
              <RefreshCw size={16} /> Reprendre
            </button>
            <button onClick={validate}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-full">
              <Check size={18} /> Utiliser cette photo
            </button>
          </>
        ) : (
          <>
            <button onClick={flip} disabled={!!error}
              className="p-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white rounded-full">
              <SwitchCamera size={20} />
            </button>
            <button onClick={takeShot} disabled={starting || !!error}
              className="w-16 h-16 bg-white rounded-full flex items-center justify-center border-4 border-gray-300 hover:bg-gray-100 disabled:opacity-40">
              <Camera size={26} className="text-gray-900" />
            </button>
            <div className="w-12" />
          </>
        )}
      </div>
    </div>
  );
};
